<template>
  <div class="score-jumbotron">
    <div class="score-scale">
      <div class="gradient"></div>
      <span class="zero">0</span>
      <span class="two">2</span>
      <span class="four">4</span>
      <span class="six">6</span>
      <span
        v-if="scorePercent !== null"
        class="grade-position"
        :style="{ left: `${scorePercent}%` }"
      ></span>
    </div>
    <div class="mt-4 pt-2">
      <span class="score-title">Score&nbsp;</span>
      <span
        v-if="displayScore !== null"
        class="score-title ms-3"
        style="color: var(--text-gray-900, #18191F);"
        >{{ displayScore }} / 6</span
      >
    </div>
  </div>
</template>

<script>
import { ref, watch } from "vue";

const MAX_RUBRIC_SCORE = 6;

export default {
  props: ["score"],
  setup(props) {
    const scorePercent = ref(null);
    const displayScore = ref(null);
    const normalizeScore = rawScore => {
      if (rawScore === null || rawScore === undefined || rawScore === "") {
        return null;
      }

      if (typeof rawScore === "number") {
        if (!Number.isFinite(rawScore)) {
          return null;
        }

        // Support both modern rubric scores (0-6) and any legacy percentages (0-100).
        if (rawScore <= MAX_RUBRIC_SCORE) {
          return rawScore;
        }

        return (rawScore / 100) * MAX_RUBRIC_SCORE;
      }

      if (typeof rawScore === "object") {
        if (Object.prototype.hasOwnProperty.call(rawScore, "overallScore")) {
          return normalizeScore(rawScore.overallScore);
        }
        if (Object.prototype.hasOwnProperty.call(rawScore, "displayScore")) {
          return normalizeScore(rawScore.displayScore);
        }
        if (Object.prototype.hasOwnProperty.call(rawScore, "score")) {
          return normalizeScore(rawScore.score);
        }

        if (
          Object.prototype.hasOwnProperty.call(rawScore, "maxScore") &&
          Object.prototype.hasOwnProperty.call(rawScore, "score")
        ) {
          const numerator = Number(rawScore.score);
          const denominator = Number(rawScore.maxScore);
          if (Number.isFinite(numerator) && Number.isFinite(denominator) && denominator > 0) {
            return (numerator / denominator) * MAX_RUBRIC_SCORE;
          }
        }
      }

      if (typeof rawScore === "string") {
        const trimmed = rawScore.trim();

        // Support backend display formats like "0/1" or "6/6" by converting to the 0-6 rubric scale.
        const fractionMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
        if (fractionMatch) {
          const numerator = Number(fractionMatch[1]);
          const denominator = Number(fractionMatch[2]);

          if (denominator > 0) {
            return (numerator / denominator) * MAX_RUBRIC_SCORE;
          }
          return null;
        }

        const fractionInsideText = trimmed.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
        if (fractionInsideText) {
          const numerator = Number(fractionInsideText[1]);
          const denominator = Number(fractionInsideText[2]);
          if (denominator > 0) {
            return (numerator / denominator) * MAX_RUBRIC_SCORE;
          }
        }

        const parsed = Number(trimmed);
        if (Number.isFinite(parsed)) {
          return normalizeScore(parsed);
        }

        const firstNumber = trimmed.match(/-?\d+(?:\.\d+)?/);
        if (firstNumber) {
          const parsedFirstNumber = Number(firstNumber[0]);
          return Number.isFinite(parsedFirstNumber)
            ? normalizeScore(parsedFirstNumber)
            : null;
        }

        return null;
      }

      return null;
    };

    const updateScoreDisplay = (rawScore) => {
      const numericScore = normalizeScore(rawScore);

      // Avoid showing a misleading score when score has not loaded yet.
      if (numericScore === null) {
        scorePercent.value = null;
        displayScore.value = null;
        return;
      }

      const score = Math.max(0, Math.min(MAX_RUBRIC_SCORE, numericScore));
      scorePercent.value = (score / MAX_RUBRIC_SCORE) * 100;
      displayScore.value = Number.isInteger(score) ? score : Number(score.toFixed(1));
    };

    watch(
      () => props.score,
      (nextScore) => {
        updateScoreDisplay(nextScore);
      },
      { immediate: true }
    );

    return { scorePercent, displayScore };
  }
};
</script>

<style scoped>
.score-title {
  color: #9b51e0;
  font-feature-settings: "liga" off;
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px; /* 93.75% */
}

.subtitle-body {
  color: var(--text-gray-900, #18191f);
  font-family: Manrope;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
}
.score-scale {
  position: relative;
}
.gradient {
  width: 100%;
  height: 16px;
  background: linear-gradient(90deg, #eb5757 0%, #f8d840 51.8%, #27ae60 100%);
  border-radius: 30px;
}
.zero,
.two,
.four,
.six {
  color: var(--text-subtle, #969bab);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px; /* 187.5% */
}
.zero {
  position: absolute;
  top: 15px;
}
.two {
  position: absolute;
  top: 15px;
  left: 33.333%;
  transform: translateX(-50%);
}
.four {
  position: absolute;
  top: 15px;
  left: 66.666%;
  transform: translateX(-50%);
}
.six {
  position: absolute;
  top: 15px;
  right: 0;
}
.grade-position {
  height: 15px;
  width: 15px;
  background-color: #474a57;
  border-radius: 50%;
  position: absolute;
  z-index: 2;
  top: 1px;
}
</style>
<style lang="sass" scoped src="@/assets/css/essayWriting.sass"></style>
